import { getApplication } from './applications';
import {
  getPublicKey,
  removeCreditKey,
  savePublicKey,
  setToken,
} from './authentication';
import { getCompany } from './companies';
import { asyncActions, error } from './general';
import { getMerchant } from './merchants';
import { getPrequalification } from './prequalifications';
import { getShoppingCart } from './shopping_carts';
import { getUser } from './users';
import * as ActionTypes from '../lib/constants/ActionTypes';

const SESSION_DEPENDENCIES = {
  prequal: true,
  cart: true,
  application: true,
  user: true,
  company: true,
  merchant: true,
};

const validateRequest = (dispatch, state) => {
  let publicKey;

  if (state) publicKey = state().sessions.get('public_key');

  if (!getPublicKey() && !publicKey) {
    dispatch(error('No session is set - public_key: ' + publicKey));
    dispatch(loading(false));
  }
};

const path = (resource) => {
  if (!resource) resource = '';
  return { resource: 'api/sessions/' + resource };
};

const publicKeyFromState = (state) => state().sessions.get('public_key');
const publicKey = (key) => (getPublicKey() ? getPublicKey() : key);

const loadDeps = (session, deps, dispatch) => {
  return Promise.all([
    deps.prequal &&
      session.loadDep('prequalified_user', dispatch, getPrequalification),
    deps.cart && session.loadDep('shopping_cart', dispatch, getShoppingCart),
    deps.application &&
      session.loadDep('application', dispatch, getApplication),
    deps.user && session.loadDep('user', dispatch, getUser),
    deps.company && session.loadDep('company', dispatch, getCompany),
    deps.merchant && session.loadDep('merchant', dispatch, getMerchant),
  ]);
};

export const loading = (bool) => ({
  type: ActionTypes.SESSION_LOADING,
  payload: bool,
});

export const sessionGuid = (string) => ({
  type: ActionTypes.SESSION_GUID,
  payload: string,
});

export const setPublicKey = (params) => {
  let key;
  return (dispatch, getState) => {
    if (
      params.public_key &&
      params.public_key !== getState().sessions.get('public_key')
    ) {
      key = params.public_key;
      savePublicKey(key);
    } else if (getPublicKey()) {
      key = getPublicKey();
    }

    return (
      key &&
      dispatch({
        type: ActionTypes.SET_PUBLIC_KEY,
        payload: key,
      })
    );
  };
};

export const clearPublicKey = () => {
  return (dispatch, getState) => {
    removeCreditKey();

    return dispatch({
      type: ActionTypes.SET_PUBLIC_KEY,
      payload: null,
    });
  };
};

export const authenticate = (public_key) => {
  return (dispatch, getState, api) => {
    validateRequest(dispatch, getState);
    const session_guid = getState().sessions.get('session_guid');

    return api
      .post(path(publicKey(public_key) + '/authenticate'), { session_guid })
      .then((res) => {
        if (res.message && res.message === 'authorized') return true;
        res && dispatch(setToken(res));
      });
  };
};

export const getRoute = (deps = {}) => {
  return (dispatch, getState, api) => {
    validateRequest(dispatch, getState);
    const public_key = getState().sessions.get('public_key');

    return api.get(path(publicKey(public_key) + '/route')).then((res) => {
      if (deps.canApplyResponse && !deps.canApplyResponse()) return null;

      return dispatch({
        type: ActionTypes.SET_SESSION,
        payload: res,
      });
    });
  };
};

export const verifyActiveSession = () => {
  return (dispatch, getState, api) => {
    validateRequest(dispatch, getState);
    const public_key = getState().sessions.get('public_key');
    const session_guid = getState().sessions.get('session_guid');

    return api
      .post(path(publicKey(public_key) + '/verify_active_session'), {
        session_guid,
      })
      .then((res) =>
        dispatch({
          type: ActionTypes.SET_SESSION,
          payload: res,
        })
      );
  };
};

export const payNowHandoff = () => (dispatch, getState, api) => {
  validateRequest(dispatch, getState);
  const public_key = getState().sessions.get('public_key');

  return api
    .post(path(publicKey(public_key) + '/pay_now_handoff'), {})
    .then((res) => {
      if (
        !res ||
        typeof res.checkout_url !== 'string' ||
        !res.checkout_url.length ||
        res.session_type !== 'pay_now'
      ) {
        throw new Error('invalid_pay_now_handoff_response');
      }
      return res;
    });
};

export const afterAuthRoute = (history, twofactor) => {
  return (dispatch, getState) => {
    const session = getState().sessions.get('session');

    if (
      twofactor ||
      (!session.workflow && !session.action) ||
      session.hasCustomFlag('recharge') ||
      session.hasCustomFlag('instore')
    ) {
      return dispatch(getRoute());
    } else {
      return history.push('/' + session.workflow + '/' + session.action);
    }
  };
};

export const shouldGetInitRoute = (session) => {
  return !session.workflow || session.workflow === '';
};

export const init = () => {
  return (dispatch, getState, api) => {
    dispatch(loading(true));
    const publicKey = getState().sessions.get('public_key');

    if (!publicKey && !getPublicKey()) {
      return Promise.reject(dispatch(loading(false)));
    }

    return dispatch(authenticate(publicKey))
      .then((res) => dispatch(getSession()))
      .then((res) => {
        if (shouldGetInitRoute(getState().sessions.get('session'))) {
          return dispatch(getRoute());
        }
        return Promise.resolve(false);
      })
      .then((res) => dispatch(loading(false)));
  };
};

export const createSession = (
  merchantKey,
  prequalificationId,
  inviteSlug,
  subDomain = []
) => {
  return (dispatch, getState, api) => {
    // also needs to add public_key to session reducer
    return api
      .post(path(), {
        slug: merchantKey,
        prequalification_id: prequalificationId,
        invite_slug: inviteSlug,
        mode: getState().sessions.get('modal')
          ? 'modal'
          : getState().sessions.get('mode'),
        custom_flags: subDomain,
      })
      .then((res) => {
        dispatch(
          asyncActions(setPublicKey(res.data.attributes), authenticate())
        );

        res.data.attributes.token &&
          dispatch(setToken(res.data.attributes.token));

        return dispatch({
          type: ActionTypes.SET_SESSION,
          payload: res,
        });
      })
      .then((res) => {
        let session = getState().sessions.get('session');
        return loadDeps(session, SESSION_DEPENDENCIES, dispatch);
      });
  };
};

export const getSession = (deps = {}) => {
  return (dispatch, getState, api) => {
    validateRequest(dispatch, getState);
    const canApplyResponse = deps.canApplyResponse || (() => true);
    const guardedDispatch = (action) => {
      if (!canApplyResponse()) return null;

      if (typeof action === 'function') {
        return action(guardedDispatch, getState, api);
      }

      return dispatch(action);
    };
    const dependencyDispatch = deps.canApplyResponse
      ? guardedDispatch
      : dispatch;

    return api
      .get(
        path(
          deps.referring
            ? getState().sessions.get('session').depId('referring_session')
            : publicKey(publicKeyFromState(getState))
        )
      )
      .then((res) => {
        if (!canApplyResponse()) return null;

        if (deps.referring) {
          return res;
        } else {
          return dispatch({
            type: ActionTypes.SET_SESSION,
            payload: res,
          });
        }
      })
      .then((res) => {
        if (deps.referring || !res || !canApplyResponse()) return res;

        let session = getState().sessions.get('session');
        return loadDeps(
          session,
          { ...SESSION_DEPENDENCIES, ...deps },
          dependencyDispatch
        );
      });
  };
};

export const updateSession = (session, deps = {}) => {
  return (dispatch, getState, api) => {
    validateRequest(dispatch, getState);

    return api
      .edit(path(publicKey(publicKeyFromState(getState))), session)
      .then((res) => {
        res.data.attributes.token &&
          dispatch(setToken(res.data.attributes.token));
        return res;
      })
      .then((res) =>
        dispatch({
          type: ActionTypes.SET_SESSION,
          payload: res,
        })
      )
      .then((res) => {
        let session = getState().sessions.get('session');
        return loadDeps(
          session,
          { ...SESSION_DEPENDENCIES, ...deps },
          dispatch
        );
      });
  };
};

export const reestablishSession = (user, token, deps = {}) => {
  return (dispatch, getState, api) => {
    return api
      .post(path(publicKey(publicKeyFromState(getState)) + '/reestablish'), {
        user_id: user.id,
      })
      .then((res) =>
        dispatch({
          type: ActionTypes.SET_SESSION,
          payload: res,
        })
      )
      .then((res) => {
        let session = getState().sessions.get('session');
        savePublicKey(session.public_key);
        dispatch(setToken(token));
        return session;
      })
      .then((res) =>
        loadDeps(res, { ...SESSION_DEPENDENCIES, ...deps }, dispatch)
      );
  };
};

export const finishSession = (type) => {
  if (!type) return Promise.reject('Missing type');

  return (dispatch, getState, api) => {
    validateRequest(dispatch, getState);

    const key = publicKey(publicKeyFromState(getState));

    return key
      ? api.edit(path(key + '/finish'), { type: type })
      : Promise.reject('missing public key');
  };
};

export const resubmitApplication = (session) => {
  return (dispatch, getState, api) => {
    validateRequest(dispatch, getState);

    return api
      .edit(
        path(publicKey(publicKeyFromState(getState)) + '/resubmit'),
        session
      )
      .then((res) =>
        dispatch({
          type: ActionTypes.SET_SESSION,
          payload: res,
        })
      )
      .then((res) => {
        let session = getState().sessions.get('session');
        return loadDeps(session, SESSION_DEPENDENCIES, dispatch);
      });
  };
};

export const existingUserEventGA = (ga, user) => {
  return (dispatch, getState) => {
    const session = getState().sessions.get('session');
    const merchant = getState()
      .entities.get('merchant')
      .get(session.depId('merchant'));

    ga.identify({
      id: user.id,
      sessionId: session.id,
      sessionKey: session.session_key,
      merchantId: merchant.id,
      publicKey: session.public_key,
    });
  };
};

export const setIsModal = (bool) => ({
  type: ActionTypes.SET_IS_MODAL,
  payload: bool,
});

export const setMode = (value) => ({
  type: ActionTypes.SET_MODE,
  payload: value,
});

export const pi4Confirm = () => ({
  type: ActionTypes.PI4_CONFIRM,
});
